import { FC, PropsWithChildren, createContext, useContext, useMemo, useState } from "react";

import {
  ReportTemplateSectionFieldType as GeneratedReportTemplateSectionFieldType,
  ReportTemplateSectionType as GeneratedReportTemplateSectionType,
  InputDependencyChoices,
  ReportSuggestionTypeConnection,
} from "@api/graphql/types-and-hooks";

export type TemplateContextType = {
  loading: boolean;
  error: Error | undefined;
  sections: ReportTemplateSection[];
  setSections: (sections: ReportTemplateSection[]) => void;
};

export type ReportTemplateSection = GeneratedReportTemplateSectionType & {
  fields: ReportTemplateSectionField[];
};

export type ReportTemplateSectionField = GeneratedReportTemplateSectionFieldType & {
  isAi: boolean;
  isGlobal?: boolean;
  isEnabled?: boolean;
  isAiGenerating?: boolean;
  description?: string;
  placeholder?: string;
  aiGenerated?: boolean;
  imageSignedUrl?: string;
  inputDependency?: InputDependencyChoices[];
  reportSuggestions?: ReportSuggestionTypeConnection;
  fieldType?: string;
  aiSources?: string;
};

const TemplateContext = createContext<TemplateContextType>({
  loading: false,
  error: undefined,
  sections: [],
  setSections: () => {},
});

export const TemplateEditorProvider: FC<PropsWithChildren> = ({ children }) => {
  const [loading] = useState<boolean>(false);
  const [error] = useState<Error | undefined>(undefined);
  const [sections, setSections] = useState<ReportTemplateSection[]>([]);

  const contextValue = useMemo(
    () => ({
      loading,
      error,
      sections,
      setSections,
    }),
    [loading, error, sections],
  );

  return <TemplateContext.Provider value={contextValue}>{children}</TemplateContext.Provider>;
};

export const useTemplate = () => useContext(TemplateContext);
